<template>
  <div class="word-list-page">
    <!-- 头部 -->
    <div class="header">
      <button class="back-btn" @click="goBack">
        <span class="back-icon">←</span>
        <span>返回</span>
      </button>
      <h1 class="page-title">单词学习</h1>
    </div>

    <!-- 进度统计 -->
    <div class="stats-bar">
      <div class="stat-item">
        <span class="stat-icon">📝</span>
        <span class="stat-text">总单词: {{ words.length }}</span>
      </div>
      <div class="stat-item">
        <span class="stat-icon">✅</span>
        <span class="stat-text">已练习: {{ practicedCount }}</span>
      </div>
    </div>

    <!-- 单词列表 -->
    <div class="word-list">
      <div
        v-for="word in words"
        :key="word.id"
        class="word-card"
        :class="{ practiced: word.practiced }"
      >
        <div class="word-main">
          <div class="word-content">
            <h3 class="word-text">{{ word.word }}</h3>
            <p class="word-phonetic">{{ word.phonetic }}</p>
            <p class="word-meaning">{{ word.meaning }}</p>
          </div>
          <div class="word-actions">
            <button
              class="action-btn play-btn"
              @click="playAudio(word)"
            >
              <span class="btn-icon">🔊</span>
            </button>
            <button
              class="action-btn record-btn"
              @click="goToRecord(word)"
            >
              <span class="btn-icon">🎤</span>
              <span class="btn-label">练习</span>
            </button>
          </div>
        </div>
        <div class="word-progress" v-if="word.bestScore > 0">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: word.bestScore + '%', background: getScoreColor(word.bestScore) }"
            ></div>
          </div>
          <span class="score-text">{{ word.bestScore }}分</span>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="words.length === 0" class="empty-state">
      <div class="empty-icon">📚</div>
      <p>暂无单词，先去读绘本吧</p>
    </div>

    <!-- 装饰 -->
    <div class="decoration">
      <div class="bird">🐦</div>
      <div class="flower">🌸</div>
    </div>
  </div>
</template>

<script>
import { getBookWords } from '@/api/book'

export default {
  name: 'WordList',
  data() {
    return {
      words: [],
      bookId: null,
      loading: false
    }
  },
  computed: {
    practicedCount() {
      return this.words.filter(w => w.practiced).length
    }
  },
  created() {
    this.bookId = this.$route.params.id
    this.fetchWords()
  },
  methods: {
    async fetchWords() {
      this.loading = true
      try {
        const res = await getBookWords(this.bookId)
        this.words = res.data || []
      } catch (error) {
        console.error('获取单词列表失败:', error)
        // 使用默认数据
        const wordsData = {
          1: [
            { id: 1, word: 'hen', phonetic: '/hen/', meaning: '母鸡' },
            { id: 2, word: 'wheat', phonetic: '/wiːt/', meaning: '小麦' },
            { id: 3, word: 'seed', phonetic: '/siːd/', meaning: '种子' },
            { id: 4, word: 'plant', phonetic: '/plænt/', meaning: '种植' },
            { id: 5, word: 'cat', phonetic: '/kæt/', meaning: '猫' },
            { id: 6, word: 'dog', phonetic: '/dɒg/', meaning: '狗' }
          ],
          2: [
            { id: 7, word: 'pig', phonetic: '/pɪg/', meaning: '猪' },
            { id: 8, word: 'house', phonetic: '/haʊs/', meaning: '房子' },
            { id: 9, word: 'straw', phonetic: '/strɔː/', meaning: '稻草' }
          ]
        }
        this.words = (wordsData[this.bookId] || wordsData[1]).map(w => ({
          ...w,
          practiced: false,
          bestScore: 0
        }))
      } finally {
        this.loading = false
      }

      // 从localStorage加载练习记录
      this.loadPracticeRecords()
    },
    loadPracticeRecords() {
      const records = JSON.parse(localStorage.getItem('studyRecords') || '[]')
      const bookRecords = records.filter(r => r.bookId == this.bookId)

      bookRecords.forEach(record => {
        const word = this.words.find(w => w.id == record.wordId)
        if (word) {
          word.practiced = true
          word.bestScore = Math.max(word.bestScore, record.score)
        }
      })
    },
    playAudio(word) {
      // 使用Web Speech API播放单词
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(word.word)
        utterance.lang = 'en-US'
        utterance.rate = 0.8
        window.speechSynthesis.speak(utterance)
      }
    },
    goToRecord(word) {
      this.$router.push(`/books/${this.bookId}/words/${word.id}/record`)
    },
    goBack() {
      this.$router.back()
    },
    getScoreColor(score) {
      if (score >= 90) return '#4CAF50'
      if (score >= 70) return '#FF9800'
      return '#F44336'
    }
  }
}
</script>

<style scoped>
.word-list-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #F3E5F5 0%, #E1BEE7 100%);
  padding-bottom: 30px;
}

/* 头部 */
.header {
  display: flex;
  align-items: center;
  padding: 15px;
  position: relative;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background: white;
  border: none;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.back-icon {
  font-size: 16px;
}

.page-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 18px;
  color: #333;
  margin: 0;
}

/* 统计栏 */
.stats-bar {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 10px 0 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
  background: white;
  padding: 8px 15px;
  border-radius: 20px;
  font-size: 13px;
  color: #666;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stat-icon {
  font-size: 16px;
}

/* 单词列表 */
.word-list {
  padding: 0 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.word-card {
  background: white;
  border-radius: 16px;
  padding: 15px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  transition: all 0.3s;
}

.word-card.practiced {
  border-left: 4px solid #4CAF50;
}

.word-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.word-content {
  flex: 1;
}

.word-text {
  font-size: 22px;
  color: #333;
  margin: 0 0 5px;
  font-weight: 600;
}

.word-phonetic {
  font-size: 14px;
  color: #9C27B0;
  margin: 0 0 3px;
}

.word-meaning {
  font-size: 13px;
  color: #888;
  margin: 0;
}

.word-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s;
}

.play-btn {
  background: #E3F2FD;
}

.record-btn {
  background: #FCE4EC;
}

.btn-icon {
  font-size: 20px;
}

.btn-label {
  font-size: 10px;
  color: #666;
  margin-top: 2px;
}

/* 进度条 */
.word-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #eee;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}

.score-text {
  font-size: 12px;
  color: #666;
  font-weight: 600;
  min-width: 40px;
  text-align: right;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 60px;
  margin-bottom: 15px;
}

.empty-state p {
  color: #999;
  font-size: 14px;
}

/* 装饰 */
.bird {
  position: fixed;
  top: 100px;
  right: 20px;
  font-size: 30px;
  opacity: 0.4;
  animation: fly 3s ease-in-out infinite;
}

.flower {
  position: fixed;
  bottom: 30px;
  left: 20px;
  font-size: 35px;
  opacity: 0.4;
  animation: sway 2s ease-in-out infinite;
}

@keyframes fly {
  0%, 100% { transform: translateY(0) translateX(0); }
  50% { transform: translateY(-10px) translateX(5px); }
}

@keyframes sway {
  0%, 100% { transform: rotate(-5deg); }
  50% { transform: rotate(5deg); }
}
</style>



// WEBPACK FOOTER //
// src/views/WordList.vue